/**
 * 下载数据对话框组件
 */

import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { getSessionsDataForExport } from '@/lib/api';
import { generateSessionsCSV, downloadCSV, formatTimeRangeText } from '@/lib/csvExport';

export default function DownloadDataDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 时间范围选项
  const timeRangeOptions = [
    { value: 7, label: '最近7天' },
    { value: 15, label: '最近15天' },
    { value: 30, label: '最近30天' },
  ];

  // 预览数据条数
  const handlePreview = async (days: number) => {
    try {
      setError(null);
      setIsLoading(true);
      const data = await getSessionsDataForExport(days);
      setPreviewCount(data.length);
    } catch (err) {
      console.error('预览数据失败:', err);
      setError('预览数据失败，请重试');
      setPreviewCount(null);
    } finally {
      setIsLoading(false);
    }
  };

  // 处理时间范围变化
  const handleDaysChange = (days: number) => {
    setSelectedDays(days);
    setPreviewCount(null);
    setError(null);
    handlePreview(days);
  };

  // 处理下载
  const handleDownload = async () => {
    try {
      setError(null);
      setIsLoading(true);

      // 获取数据
      const data = await getSessionsDataForExport(selectedDays);

      if (data.length === 0) {
        setError(`所选时间段（${formatTimeRangeText(selectedDays)}）内没有直播数据`);
        setIsLoading(false);
        return;
      }

      // 生成CSV
      const csvContent = generateSessionsCSV(data);

      // 生成文件名
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0].replace(/-/g, '');
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '');
      const filename = `直播数据_${formatTimeRangeText(selectedDays)}_${dateStr}_${timeStr}`;

      // 下载文件
      downloadCSV(csvContent, filename);

      // 关闭对话框
      setIsOpen(false);
      
      // 重置状态
      setTimeout(() => {
        setPreviewCount(null);
        setError(null);
      }, 300);
    } catch (err) {
      console.error('下载数据失败:', err);
      setError('下载数据失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 对话框打开时自动预览
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      handlePreview(selectedDays);
    } else {
      // 关闭时重置状态
      setPreviewCount(null);
      setError(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="lg" className="w-full gap-2 sm:w-auto">
          <Download className="h-4 w-4" />
          下载直播数据
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>下载直播数据</DialogTitle>
          <DialogDescription>
            导出指定时间范围内的直播场次统计数据为 CSV 文件。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* 时间范围选择 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[var(--text-soft)]">
              选择时间范围
            </label>
            <div className="flex gap-2">
              {timeRangeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleDaysChange(option.value)}
                  disabled={isLoading}
                  className={`min-h-11 flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    selectedDays === option.value
                      ? 'bg-[var(--action-fill)] text-[var(--action-contrast)]'
                      : 'border border-[var(--border-control)] bg-[var(--panel-deep)] text-[var(--text-muted)] hover:text-[var(--text-soft)]'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* 数据预览 */}
          <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--panel-deep)] p-4">
            {isLoading && previewCount === null ? (
              <div className="flex items-center justify-center gap-2 text-[var(--text-muted)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">正在统计数据...</span>
              </div>
            ) : previewCount !== null ? (
              <div className="text-center">
                <p className="text-sm text-[var(--text-muted)]">
                  {formatTimeRangeText(selectedDays)}内共有
                </p>
                <p className="my-1 font-mono text-3xl font-semibold text-[var(--action-blue)]">
                  {previewCount}
                </p>
                <p className="text-sm text-[var(--text-muted)]">条直播场次数据</p>
              </div>
            ) : (
              <div className="text-center text-sm text-[var(--text-muted)]">
                选择时间范围后显示数据统计
              </div>
            )}
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="console-alert-warning p-3">
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* 说明信息 */}
          <div className="space-y-1 text-xs leading-5 text-[var(--text-muted)]">
            <p className="text-[var(--text-soft)]">导出说明</p>
            <p>• CSV文件包含场次基本信息和统计数据</p>
            <p>• 时间范围基于直播的实际开始时间</p>
            <p>• 仅导出已开始的直播场次</p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={isLoading}
          >
            取消
          </Button>
          <Button
            onClick={handleDownload}
            disabled={isLoading || previewCount === 0}
            className="gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                下载中...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                下载CSV
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
